import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Divider,
  FormControlLabel,
  InputAdornment,
  RadioGroup,
  Theme,
  useTheme,
} from "@mui/material";
import { GridColDef, GridToolbarContainer } from "@mui/x-data-grid-premium";
import React, { useEffect, useState } from "react";

import { OutlinedButton, PrimaryButton } from "@/Components/v2/atoms/Button";
import { Checkbox } from "@/Components/v2/atoms/Checkbox";
import { FormControl } from "@/Components/v2/atoms/FormControl";
import { Menu } from "@/Components/v2/atoms/Menu";
import { Radio } from "@/Components/v2/atoms/Radio";
import { Stack } from "@/Components/v2/atoms/Stack";
import { TextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";
import { SingleSelectV2 } from "@/Components/v2/molecule/LabelledAutoComplete/v2/SingleSelectV2";
import { OptionV2 } from "@/Components/v2/molecule/LabelledAutoComplete/v2/types";
import { colors } from "@/Components/v2/utils/brand-colors";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useFileName } from "@/hooks/useFileName";
import { useModuleName } from "@/hooks/useModuleName";

import { BusinessUnitToggle, MetricType } from "../../types";

export type Toggle = {
  id: string;
  selectedValue: string;
  options: {
    label: string;
    value: string;
  }[];
};

export type MetricDropdownConfig = {
  label: string;
  value: string;
  options: OptionV2[];
  onChange: (value: string | null) => void;
};

export type FilterSection = {
  id: string;
  title: string;
  items: Array<{
    id: string;
    title: string;
    color: string;
    isActive: boolean;
  }>;
};

interface ToolBarProps {
  apiRef: React.MutableRefObject<any>;
  columns: GridColDef[];
  activeColumns: string[];
  toggles?: Toggle[];
  metricDropdown?: MetricDropdownConfig;
  tableName?: string;
  fileName?: string;
  onDownload?: () => void;
  handleApplyFilters: ({
    activeColumns,
    toggles,
  }: {
    activeColumns: string[];
    toggles: { [id: string]: string };
  }) => void;
  handleResetFilters: () => void;
  useGroupedFilters?: boolean;
  groupedFilterSections?: FilterSection[];
}

// MUI Menu item applies a linear gradient to its background - we need to replicate this for the 'Apply' button background.
const BG_PROPS = (theme: Theme) => ({
  backgroundColor: theme.palette.background.paper,
  backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12))",
});

export const ToolBarShared: React.FC<ToolBarProps> = ({
  apiRef,
  columns,
  activeColumns,
  toggles,
  metricDropdown,
  fileName,
  onDownload,
  handleApplyFilters,
  handleResetFilters,
  tableName = "Insights Table",
  useGroupedFilters = false,
  groupedFilterSections,
}) => {
  const theme = useTheme();
  const { fileName: defaultFileName } = useFileName();
  const { moduleName } = useModuleName();
  const { unitsSelected, insightsTableDownloaded, splitByBusinessUnit } = useAnalytics();

  const [internalActiveColumns, setInternalActiveColumns] = useState<string[]>(activeColumns || []);
  const [searchText, setSearchText] = useState("");
  const [internalToggles, setInternalToggles] = useState<{ [id: string]: string }>(
    toggles?.reduce(
      (acc, toggle) => {
        acc[toggle.id] = toggle.selectedValue;
        return acc;
      },
      {} as { [id: string]: string }
    ) || {}
  );
  const [internalExpandedSections, setInternalExpandedSections] = useState<string[]>(
    groupedFilterSections?.map((s) => s.id) || []
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (useGroupedFilters && groupedFilterSections) {
      setInternalExpandedSections(groupedFilterSections.map((s) => s.id));
    }
  }, [useGroupedFilters, groupedFilterSections]);

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      insightsTableDownloaded({ moduleName });
    }
    exportDataToCsv();
  };

  const exportDataToCsv = () => {
    apiRef.current.exportDataAsCsv({
      fileName: fileName || defaultFileName,
    });
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleActiveColumnsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInternalActiveColumns(
      event.target.checked
        ? [...internalActiveColumns, event.target.name]
        : internalActiveColumns.filter((column) => column !== event.target.name)
    );
  };

  const handleToggleChange = (id: string, value: string) => {
    setInternalToggles((prev) => {
      return {
        ...prev,
        [id]: value,
      };
    });
  };

  const handleApply = () => {
    handleApplyFilters({
      activeColumns: internalActiveColumns,
      toggles: internalToggles,
    });
    if (internalToggles["businessUnitToggle"] === BusinessUnitToggle.SPLIT) {
      splitByBusinessUnit({ moduleName });
    }
    setSearchText("");
    setAnchorEl(null);
  };

  const showingColumns = columns.filter((column) => activeColumns.includes(column.field));
  const columnsNotInActiveColumns = columns.filter(
    (column) => !activeColumns.includes(column.field)
  );

  // Filter columns based on search text
  const filteredShowingColumns = searchText.trim()
    ? showingColumns.filter((column) =>
        column.headerName?.toString().toLowerCase().includes(searchText.toLowerCase())
      )
    : showingColumns;

  const filteredAvailableColumns = searchText.trim()
    ? columnsNotInActiveColumns.filter((column) =>
        column.headerName?.toString().toLowerCase().includes(searchText.toLowerCase())
      )
    : columnsNotInActiveColumns;

  const handleMetricDropdownChange = (value: string | null) => {
    if (!value) return;
    if (value === MetricType.SALES_UNITS) {
      unitsSelected({ moduleName });
    }
    metricDropdown?.onChange(value);
  };

  const getFilteredGroupedSections = () => {
    if (!groupedFilterSections) return [];

    return searchText.trim()
      ? groupedFilterSections
          .map((section) => ({
            ...section,
            items: section.items.filter((item) =>
              item.title.toLowerCase().includes(searchText.toLowerCase())
            ),
          }))
          .filter((section) => section.items.length > 0)
      : groupedFilterSections;
  };

  const renderGroupedSections = () => {
    const filteredSections = getFilteredGroupedSections();

    return filteredSections.map((section, index) => (
      <Box key={section.id}>
        <Stack direction="column" gap={1}>
          {/* Section header */}
          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            <Typography variant="body1" fontSize={14} fontWeight={600}>
              {section.title}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {/* Don't show action buttons for metadata section */}
              {section.id !== "metadata" && (
                <>
                  <OutlinedButton
                    size="small"
                    sx={{
                      fontSize: 12,
                      px: 1,
                      minWidth: 0,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                    onClick={() => {
                      const columnIds = section.items.map((i) => i.id);
                      const metadataSection = groupedFilterSections?.find(
                        (s) => s.id === "metadata"
                      );
                      const metadataColumnIds = metadataSection?.items.map((i) => i.id) ?? [];
                      setInternalActiveColumns([...metadataColumnIds, ...columnIds]);
                    }}
                  >
                    Show Only
                  </OutlinedButton>
                  {(() => {
                    const allDisabled = section.items.every(
                      (item) => !internalActiveColumns.includes(item.id)
                    );
                    return (
                      <OutlinedButton
                        size="small"
                        sx={{
                          fontSize: 12,
                          px: 1,
                          minWidth: 0,
                          height: 28,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                        onClick={() => {
                          const columnIds = section.items.map((i) => i.id);
                          if (allDisabled) {
                            setInternalActiveColumns((prev) => [
                              ...new Set([...prev, ...columnIds]),
                            ]);
                          } else {
                            setInternalActiveColumns((prev) =>
                              prev.filter((c) => !columnIds.includes(c))
                            );
                          }
                        }}
                      >
                        {allDisabled ? "Show All" : "Hide All"}
                      </OutlinedButton>
                    );
                  })()}
                </>
              )}
              <Box
                onClick={() => {
                  setInternalExpandedSections((prev) =>
                    prev.includes(section.id)
                      ? prev.filter((s) => s !== section.id)
                      : [...prev, section.id]
                  );
                }}
                sx={{ cursor: "pointer" }}
              >
                {internalExpandedSections.includes(section.id) ? (
                  <KeyboardArrowUp fontSize="small" />
                ) : (
                  <KeyboardArrowDown fontSize="small" />
                )}
              </Box>
            </Box>
          </Stack>

          {/* Section items with checkboxes */}
          {internalExpandedSections.includes(section.id) && (
            <FormControl>
              <RadioGroup sx={{ "& .MuiFormControlLabel-root": { height: "30px" } }}>
                {section.items.map((item) => (
                  <FormControlLabel
                    key={item.id}
                    value={item.id}
                    label={item.title}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "14px",
                        fontWeight: internalActiveColumns.includes(item.id) ? 600 : 400,
                        maxWidth: "300px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "inline-block",
                        verticalAlign: "middle",
                      },
                      "& .MuiButtonBase-root": {
                        paddingRight: "6px",
                      },
                    }}
                    control={
                      <Checkbox
                        checked={internalActiveColumns.includes(item.id)}
                        onChange={() => {
                          setInternalActiveColumns((prev) =>
                            prev.includes(item.id)
                              ? prev.filter((c) => c !== item.id)
                              : [...prev, item.id]
                          );
                        }}
                        size="small"
                      />
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </Stack>
        {index < filteredSections.length - 1 && <Divider />}
      </Box>
    ));
  };

  return (
    <>
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "space-between",
          paddingBottom: "0.75rem",
          position: "sticky",
          zIndex: 10,
          top: 0,
          backgroundColor: theme.palette.mode === "dark" ? colors.lightBlack : colors.white,
        }}
      >
        <Typography fontSize={"1.5rem"} fontWeight={600}>
          {tableName}
        </Typography>
        <Stack spacing={1} direction={"row"} alignItems="center">
          {metricDropdown && (
            <SingleSelectV2
              label={metricDropdown.label}
              id="metric-dropdown"
              options={metricDropdown.options}
              value={metricDropdown.value}
              onChange={handleMetricDropdownChange}
              width={240}
              multiple={false}
              disableClearable
              sx={{
                "& .MuiAutocomplete-input": {
                  fontSize: "14px",
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                },
              }}
            />
          )}
          <OutlinedButton
            aria-controls={open ? "simple-menu" : undefined}
            aria-haspopup="true"
            onClick={handleClick}
            variant="outlined"
            startIcon={<FilterListIcon />}
          >
            Filter
          </OutlinedButton>

          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              style: {
                maxHeight: "80vh",
                width: useGroupedFilters ? 400 : 350,
              },
            }}
            sx={{
              "& .MuiList-root": {
                paddingBottom: 0,
              },
            }}
          >
            <Stack direction="column" height="100%" width="100%" sx={BG_PROPS(theme)}>
              {/* Common: Search bar (always shown) */}
              <Stack padding={2} paddingBottom={0}>
                <TextField
                  placeholder="Search"
                  value={searchText}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              {/* Common: Toggles section (always shown if provided) */}
              {toggles && toggles.length > 0 && (
                <>
                  <Stack padding={2} paddingBottom={0}>
                    <Stack spacing={0}>
                      {toggles.map((toggle, index) => (
                        <Box key={toggle.id}>
                          <Box sx={{ py: 1 }}>
                            <RadioGroup
                              aria-labelledby={`${toggle.id}-label`}
                              name={toggle.id}
                              value={internalToggles[toggle.id]}
                              onChange={(e) => handleToggleChange(toggle.id, e.target.value)}
                            >
                              {toggle.options.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  sx={{
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "14px",
                                      maxWidth: "300px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "inline-block",
                                      verticalAlign: "middle",
                                    },
                                    margin: 0,
                                  }}
                                  value={option.value}
                                  control={
                                    <Radio
                                      sx={{
                                        padding: "4px 8px 4px 0px",
                                      }}
                                    />
                                  }
                                  label={option.label}
                                />
                              ))}
                            </RadioGroup>
                          </Box>
                          {index < toggles.length - 1 && <Divider />}
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                  <Divider />
                </>
              )}

              {/* CONDITIONAL: Grouped sections OR flat list */}
              <Stack
                padding={2}
                spacing={useGroupedFilters ? 2 : 1}
                flex={1}
                sx={{
                  overflowY: "auto",
                }}
              >
                {(() => {
                  // Check if there are no results for either mode
                  const hasNoResults =
                    useGroupedFilters && groupedFilterSections
                      ? getFilteredGroupedSections().length === 0
                      : filteredShowingColumns.length === 0 &&
                        filteredAvailableColumns.length === 0;

                  if (hasNoResults) {
                    return (
                      <Typography fontSize={12}>
                        No results found. Check your spelling or try a different keyword.
                      </Typography>
                    );
                  }

                  // Otherwise, render grouped or flat sections
                  return useGroupedFilters && groupedFilterSections ? (
                    renderGroupedSections()
                  ) : (
                    <>
                      {filteredShowingColumns.length > 0 && (
                        <div>
                          <Divider />
                          <Typography
                            variant="subtitle1"
                            paddingY={1}
                            fontWeight={600}
                            fontSize={12}
                          >
                            Showing:
                          </Typography>
                          <FormControl>
                            <RadioGroup sx={{ "& .MuiFormControlLabel-root": { height: "30px" } }}>
                              {filteredShowingColumns.map((column) => (
                                <FormControlLabel
                                  key={column.field}
                                  value={column.field}
                                  label={column.headerName}
                                  sx={{
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      maxWidth: "240px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "inline-block",
                                      verticalAlign: "middle",
                                    },
                                    "& .MuiButtonBase-root": {
                                      paddingRight: "6px",
                                    },
                                  }}
                                  control={
                                    <Checkbox
                                      checked={internalActiveColumns.includes(column.field)}
                                      onChange={(event: any) => handleActiveColumnsChange(event)}
                                      name={column.field}
                                      size="small"
                                    />
                                  }
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </div>
                      )}
                      {filteredAvailableColumns.length > 0 && (
                        <>
                          <Divider />
                          <Typography variant="subtitle1" fontWeight={600} fontSize={12}>
                            All Values:
                          </Typography>
                          <FormControl>
                            <RadioGroup sx={{ "& .MuiFormControlLabel-root": { height: "30px" } }}>
                              {filteredAvailableColumns.map((column) => (
                                <FormControlLabel
                                  key={column.field}
                                  value={column.field}
                                  label={column.headerName}
                                  sx={{
                                    "& .MuiFormControlLabel-label": {
                                      fontSize: "14px",
                                      fontWeight: internalActiveColumns.includes(column.field)
                                        ? 600
                                        : 400,
                                      maxWidth: "240px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "inline-block",
                                      verticalAlign: "middle",
                                    },
                                    "& .MuiButtonBase-root": {
                                      paddingRight: "6px",
                                    },
                                  }}
                                  control={
                                    <Checkbox
                                      checked={internalActiveColumns.includes(column.field)}
                                      onChange={(event: any) => handleActiveColumnsChange(event)}
                                      name={column.field}
                                      size="small"
                                    />
                                  }
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </>
                      )}
                    </>
                  );
                })()}
              </Stack>

              {/* Common: Apply and Reset buttons (always shown) */}
              <Stack
                padding={2}
                borderTop="1px solid"
                borderColor="divider"
                position="sticky"
                bottom="0"
                zIndex="1"
                sx={BG_PROPS(theme)}
              >
                <PrimaryButton
                  size="small"
                  onClick={handleApply}
                  sx={{ fontSize: "14px", width: "100%" }}
                >
                  Apply
                </PrimaryButton>
                <OutlinedButton
                  size="small"
                  onClick={handleResetFilters}
                  sx={{ fontSize: "14px", width: "100%", mt: 1 }}
                >
                  Reset Filters
                </OutlinedButton>
              </Stack>
            </Stack>
          </Menu>
          <OutlinedButton variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
            Download
          </OutlinedButton>
        </Stack>
      </GridToolbarContainer>
      <Divider sx={{ my: 3 }} />
    </>
  );
};
